/**
 * Localization strings used for Interactions
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const interactionsText = createDictionary({
  interactions: {
    'en-us': 'Interactions',
    'ru-ru': 'Взаимодействия',
    'es-es': 'Interacciones',
    'fr-fr': 'Interactions',
    'uk-ua': 'Взаємодії',
    'de-ch': 'Interaktionen',
    'pt-br': 'Interações',
  },
  addItems: {
    'en-us': 'Add Items',
    'ru-ru': 'Добавить элементы',
    'es-es': 'Agregar elementos',
    'fr-fr': 'Ajouter des objets',
    'uk-ua': 'Додати елементи',
    'de-ch': 'Elemente hinzufügen',
    'pt-br': 'Adicionar itens',
  },
  recordReturn: {
    'en-us': '{table:string} Return',
    'ru-ru': '{table:string} Возврат',
    'es-es': '{table:string} Regresar',
    'fr-fr': '{table:string} Retour',
    'uk-ua': '{table:string} Повернення',
    'de-ch': '{table:string} Zurück',
    'pt-br': '{table:string} Retornar',
  },
  preparationsNotFoundFor: {
    'en-us': 'No preparations were found for the following records:',
    'de-ch': 'Für die folgenden Datensätze wurden keine Präparate gefunden:',
    'es-es': 'No se encontraron preparaciones para los siguientes registros:',
    'fr-fr':
      "Aucune préparation n'a été trouvée pour les enregistrements suivants :",
    'ru-ru': 'Препаратов для следующих записей не обнаружено:',
    'uk-ua': 'Для наступних записів не знайдено жодних підготовчих матеріалів:',
    'pt-br': 'Não foram encontradas preparações para os seguintes registros:',
  },
  preparationsNotAvailableFor: {
    'en-us':
      'No preparations are available for at least one type of preparation in the following records:',
    'de-ch':
      'Für mindestens eine Präparateart sind in folgenden Datensätzen keine Präparate vorhanden:',
    'es-es':
      'No hay preparaciones disponibles para al menos un tipo de preparación en los siguientes registros:',
    'fr-fr':
      "Aucune préparation n'est disponible pour au moins un type de préparation dans les enregistrements suivants :",
    'ru-ru': 'В следующих записях отсутствуют препараты хотя бы одного типа:',
    'uk-ua':
      'У наступних записах відсутні препарати принаймні для одного типу препарату:',
    'pt-br':
      'Não há preparações disponíveis para pelo menos um tipo de preparação nos seguintes registros:',
  },
  problemsFound: {
    'en-us': 'There are problems with the entry:',
    'ru-ru': 'Возникли проблемы с записью:',
    'es-es': 'Hay problemas con la entrada:',
    'fr-fr': 'Il y a des problèmes avec la saisie :',
    'uk-ua': 'Є проблеми зі вступом:',
    'de-ch': 'Es gibt Probleme mit dem Eintrag:',
    'pt-br': 'Há problemas com a entrada:',
  },
  byChoosingRecordSet: {
    comment: 'See documentation for syntax for plural rules',
    'en-us': 'By choosing a recordset ({{count:none | one | ??}} available)',
    'ru-ru': 'Выбрав набор записей (доступно {{count:none | one | ??}})',
    'es-es':
      'Al elegir un conjunto de registros ({{count:none | one | ??}} disponibles)',
    'fr-fr':
      "En choisissant un jeu d'enregistrements ({{count:none | one | ??}} disponible)",
    'uk-ua': 'Вибравши набір записів (доступно {{count:none | one | ??}})',
    'de-ch':
      'Durch wählen eines Datensatzes ({{count:none | one | ??}} available)',
    'pt-br':
      'Escolhendo um conjunto de registros ({{count:none | one | ??}} disponível)',
  },
  byEnteringNumbers: {
    comment: `
      Field name is localized. Coming from Schema Configuration. I.e, By
      entering Catalog Numbers
    `,
    'en-us': 'By entering {fieldName:string}s',
    'ru-ru': 'Введя {fieldName:string}s',
    'es-es': 'Ingresando {fieldName:string}s',
    'fr-fr': 'En saisissant les {fieldName:string}',
    'uk-ua': 'Ввівши {fieldName:string}s',
    'de-ch': 'Durch Eingabe von {fieldName:string}s',
    'pt-br': 'Ao inserir {fieldName:string}s',
  },
  withoutPreparations: {
    'en-us': 'Without preparations',
    'ru-ru': 'Без подготовки',
    'es-es': 'Sin preparaciones',
    'fr-fr': 'Sans préparations',
    'uk-ua': 'Без підготовки',
    'de-ch': 'Ohne Präparate',
    'pt-br': 'Sem preparativos',
  },
  continueWithoutPreparations: {
    'en-us': 'Continue without preparations',
    'de-ch': 'Ohne Vorbereitungen weitermachen',
    'es-es': 'Continuar sin preparaciones',
    'fr-fr': 'Continuer sans préparation',
    'ru-ru': 'Продолжить без подготовки',
    'uk-ua': 'Продовжуйте без підготовки',
    'pt-br': 'Continuar sem preparações',
  },
  addUnassociated: {
    'en-us': 'Add unassociated item',
    'ru-ru': 'Добавить несвязанный элемент',
    'es-es': 'Agregar elemento no asociado',
    'fr-fr': 'Ajouter un objet non associé',
    'uk-ua': "Додати непов'язаний елемент",
    'de-ch': 'Nicht assoziierter Gegenstand hinzufügen',
    'pt-br': 'Adicionar item não associado',
  },
  preparations: {
    'en-us': 'Preparations',
    'ru-ru': 'Препараты',
    'es-es': 'Preparaciones',
    'fr-fr': 'Preparations',
    'uk-ua': 'Підготовка',
    'de-ch': 'Präparate',
    'pt-br': 'Preparativos',
  },
  preparationsCanNotBeReturned: {
    'en-us': 'Preparations cannot be returned in this context.',
    'ru-ru': 'Препараты в данном случае возврату не подлежат.',
    'es-es': 'En este contexto no es posible retomar las preparaciones',
    'fr-fr': 'Les preparations ne peuvent être renvoyées dans ce contexte.',
    'uk-ua': 'У цьому контексті препарати не можна повернути.',
    'de-ch': 'Präparate können in diesem Kontext nicht zurückgegeben werden.',
    'pt-br': 'Os preparativos não podem ser devolvidos neste contexto.',
  },
  noUnresolvedPreparations: {
    'en-us': 'There are no unresolved preparations for this loan.',
    'ru-ru':
      'Никаких нерешенных подготовительных вопросов по данному кредиту нет.',
    'es-es': 'No existen preparaciones pendientes para este préstamo.',
    'fr-fr': 'Il n’y a pas de preparations non retournées pour ce prêt.',
    'uk-ua':
      'Немає жодних невирішених питань щодо підготовки до цього кредиту.',
    'de-ch': 'Für dieses Ausleihe bestehen keine ungelösten Vorbereitungen.',
    'pt-br': 'Não há preparativos não resolvidos para este empréstimo.',
  },
  unresolved: {
    'en-us': 'Unresolved',
    'ru-ru': 'Нерешенный',
    'es-es': 'Sin resolver',
    'fr-fr': 'Non résolu',
    'uk-ua': 'Невирішено',
    'de-ch': 'Ungelöst',
    'pt-br': 'Não resolvido',
  },
  return: {
    comment: 'Verb',
    'en-us': 'Return',
    'ru-ru': 'Возвращаться',
    'es-es': 'Devolver',
    'fr-fr': 'Retour',
    'uk-ua': 'Повернення',
    'de-ch': 'Rückgabe',
    'pt-br': 'Retornar',
  },
  resolve: {
    comment: 'As in "Resolve preparations"',
    'en-us': 'Resolve',
    'ru-ru': 'Решать',
    'es-es': 'Resolver',
    'fr-fr': 'Résoudre',
    'uk-ua': 'Вирішити',
    'de-ch': 'Lösen',
    'pt-br': 'Resolver',
  },
  returnAllPreparations: {
    'en-us': 'Return all preparations',
    'ru-ru': 'Вернуть все препараты',
    'es-es': 'Devolver todos los preparativos',
    'fr-fr': 'Retourner toutes les préparations',
    'uk-ua': 'Поверніть усі заготовки',
    'de-ch': 'Alle Präparate zurückgeben',
    'pt-br': 'Devolva todos os preparativos',
  },
  returnSelectedPreparations: {
    'en-us': 'Return selected preparations',
    'ru-ru': 'Возврат выбранных препаратов',
    'es-es': 'Devolver preparaciones seleccionadas',
    'fr-fr': 'Retourner les préparations sélectionnées',
    'uk-ua': 'Повернути вибрані препарати',
    'de-ch': 'Ausgewählte Präparate zurückgeben',
    'pt-br': 'Devolver preparações selecionadas',
  },
  selectAllAvailablePreparations: {
    'en-us': 'Select all available preparations',
    'ru-ru': 'Выбрать все доступные препараты',
    'es-es': 'Seleccione todas las preparaciones disponibles',
    'fr-fr': 'Sélectionnez toutes les préparations disponibles',
    'uk-ua': 'Виберіть усі доступні препарати',
    'de-ch': 'Alle verfügbaren Präparate auswählen',
    'pt-br': 'Selecione todas as preparações disponíveis',
  },
  selectAll: {
    'en-us': 'Select All',
    'ru-ru': 'Выбрать все',
    'es-es': 'Seleccionar todo',
    'fr-fr': 'Tout sélectionner',
    'uk-ua': 'Вибрати все',
    'de-ch': 'Alle auswählen',
    'pt-br': 'Selecionar tudo',
  },
  selectedAmount: {
    'en-us': 'Selected Amount',
    'ru-ru': 'Выбранная сумма',
    'es-es': 'Cantidad seleccionada',
    'fr-fr': 'Quantité sélectionnée',
    'uk-ua': 'Вибрана сума',
    'de-ch': 'Ausgewählte Anzahl',
    'pt-br': 'Quantidade selecionada',
  },
  returnedAmount: {
    'en-us': 'Returned Amount',
    'ru-ru': 'Возвращенная сумма',
    'es-es': 'Cantidad devuelta',
    'fr-fr': 'Quantité retournée',
    'uk-ua': 'Повернена сума',
    'de-ch': 'Zurückgegebene Anzahl',
    'pt-br': 'Valor Devolvido',
  },
  resolvedAmount: {
    'en-us': 'Resolved Amount',
    'ru-ru': 'Решенная сумма',
    'es-es': 'Monto Resuelto',
    'fr-fr': 'Montant résolu',
    'uk-ua': 'Вирішена сума',
    'de-ch': 'Gelöste Anzahl',
    'pt-br': 'Valor resolvido',
  },
  prepReturnFormatter: {
    comment: 'Used to format preparations in the prep return dialog',
    'en-us': '{tableName:string}: {resource:string}',
    'ru-ru': '{tableName:string}: {resource:string}',
    'es-es': '{tableName:string}: {resource:string}',
    'fr-fr': '{tableName:string}: {resource:string}',
    'uk-ua': "{tableName:string}': {resource:string}",
    'de-ch': '{tableName:string}: {resource:string}',
    'pt-br': '{tableName:string}: {resource:string}',
  },
  resolvedLoans: {
    comment: 'Example: Resolved Loan records',
    'en-us': 'Resolved {loanTable:string} records',
    'es-es': 'Registros {loanTable:string} resueltos',
    'fr-fr': 'Enregistrements résolus {loanTable:string}',
    'ru-ru': 'Решенные {loanTable:string} записи',
    'uk-ua': 'Вирішено записів {loanTable:string}',
    'de-ch': 'Aufgelöste {loanTable:string}-Datensätze',
    'pt-br': 'Registros resolvidos {loanTable:string}',
  },
  openLoans: {
    comment: 'Example: Open Loan records',
    'en-us': 'Open {loanTable:string} records',
    'es-es': 'Abrir {loanTable:string} registros',
    'fr-fr': 'Ouvrir les enregistrements {loanTable:string}',
    'ru-ru': 'Открыть {loanTable:string} записи',
    'uk-ua': 'Відкрити записи {loanTable:string}',
    'de-ch': '{loanTable:string}-Datensätze öffnen',
    'pt-br': 'Abrir registros {loanTable:string}',
  },
  gifts: {
    comment: 'Example: Gift records',
    'en-us': '{giftTable:string} records',
    'es-es': '{giftTable:string} registros',
    'fr-fr': '{giftTable:string} enregistrements',
    'ru-ru': '{giftTable:string} записей',
    'uk-ua': '{giftTable:string} записи',
    'de-ch': '{giftTable:string} Datensätze',
    'pt-br': '{giftTable:string} registros',
  },
  exchanges: {
    comment: 'Example: Exchange In / Exchnage Out records',
    'en-us': '{exhangeInTable:string} / {exhangeOutTable:string} records',
    'es-es': '{exhangeInTable:string} / {exhangeOutTable:string} registros',
    'fr-fr':
      '{exhangeInTable:string} / {exhangeOutTable:string} enregistrements',
    'ru-ru': '{exhangeInTable:string} / {exhangeOutTable:string} записи',
    'uk-ua': 'Записи {exhangeInTable:string} / {exhangeOutTable:string}',
    'de-ch': '{exhangeInTable:string} / {exhangeOutTable:string} Datensätze',
    'pt-br': '{exhangeInTable:string} / {exhangeOutTable:string} registros',
  },
  unCataloged: {
    'en-us': 'uncataloged',
    'ru-ru': 'некаталогизированный',
    'es-es': 'sin catalogar',
    'fr-fr': 'non catalogué',
    'uk-ua': 'некаталогізований',
    'de-ch': 'nicht katalogisiert',
    'pt-br': 'não catalogado',
  },
  returnedPreparations: {
    comment: 'Example: Preparation records',
    'en-us': 'Returned {tablePreparation:string} records',
    'es-es': 'Se devolvieron {tablePreparation:string} registros',
    'fr-fr': 'Enregistrements {tablePreparation:string} renvoyés',
    'ru-ru': 'Возвращено {tablePreparation:string} записей',
    'uk-ua': 'Повернуто записів {tablePreparation:string}',
    'de-ch': 'Zurückgegebene {tablePreparation:string} Datensätze',
    'pt-br': 'Registros retornados {tablePreparation:string}',
  },
  returnedAndSaved: {
    comment: 'Example: 2 Preparation records have been returned and saved',
    'en-us':
      '{count:number|formatted} {tablePreparation:string} records have been returned and saved',
    'es-es':
      '{count:number|formatted} {tablePreparation:string} registros han sido devueltos y guardados',
    'fr-fr':
      '{count:number|formatted} {tablePreparation:string} enregistrements ont été renvoyés et enregistrés',
    'ru-ru':
      '{count:number|formatted} {tablePreparation:string} записей были возвращены и сохранены',
    'uk-ua':
      'Повернуто та збережено записів {count:number|formatted} {tablePreparation:string}',
    'de-ch':
      '{count:number|formatted} {tablePreparation:string} Datensätze wurden zurückgegeben und gespeichert',
    'pt-br':
      '{count:number|formatted} {tablePreparation:string} registros foram retornados e salvos',
  },
  deselectAll: {
    'en-us': 'Deselect all',
    'ru-ru': 'Отменить выбор всех',
    'es-es': 'Deseleccionar todo',
    'fr-fr': 'Désélectionner tout',
    'uk-ua': 'Зняти вибір усіх',
    'de-ch': 'Alle abwählen',
    'pt-br': 'Desmarcar tudo',
  },
  available: {
    'en-us': 'Available',
    'ru-ru': 'Доступный',
    'es-es': 'Disponible',
    'fr-fr': 'Disponible',
    'uk-ua': 'Доступно',
    'de-ch': 'Verfügbar',
    'pt-br': 'Disponível',
  },
  unavailable: {
    'en-us': 'Unavailable',
    'ru-ru': 'Недоступно',
    'es-es': 'Indisponible',
    'fr-fr': 'Indisponible',
    'uk-ua': 'Недоступно',
    'de-ch': 'Nicht verfügbar',
    'pt-br': 'Indisponível',
  },
  returnLoan: {
    comment: 'Example: Return Loan records',
    'en-us': 'Return {tableLoan:string} records',
    'es-es': 'Devolver {tableLoan:string} registros',
    'fr-fr': 'Renvoyer les enregistrements {tableLoan:string}',
    'ru-ru': 'Возврат {tableLoan:string} записей',
    'uk-ua': 'Повернути записи {tableLoan:string}',
    'de-ch': '{tableLoan:string} Datensätze zurückgeben',
    'pt-br': 'Retornar {tableLoan:string} registros',
  },
  createRecord: {
    comment: 'Example: Create a Loan',
    'en-us': 'Create {table:string}',
    'es-es': 'Crear {table:string}',
    'fr-fr': 'Créer {table:string}',
    'ru-ru': 'Создать {table:string}',
    'uk-ua': 'Створити {table:string}',
    'de-ch': 'Erstelle {table:string}',
    'pt-br': 'Criar {table:string}',
  },
  noPreparationsWarning: {
    'en-us':
      'None of these objects have preparations. Would you like to continue?',
    'de-ch':
      'Für keines dieser Objekte liegen Vorbereitungen vor. Möchten Sie fortfahren?',
    'es-es':
      'Ninguno de estos objetos tiene preparaciones. ¿Quieres continuar?',
    'fr-fr': "Aucun de ces objets n'a été préparé. Voulez-vous continuer ?",
    'ru-ru': 'Ни один из этих объектов не имеет подготовки. Хотите продолжить?',
    'uk-ua': "Жоден з цих об'єктів не має підготовки. Бажаєте продовжити?",
    'pt-br': 'Nenhum destes objetos possui preparação. Deseja continuar?',
  },
  continue: {
    'en-us': 'Continue',
    'de-ch': 'Weitermachen',
    'es-es': 'Continuar',
    'fr-fr': 'Continuer',
    'ru-ru': 'Продолжать',
    'uk-ua': 'Продовжити',
    'pt-br': 'Continuar',
  },
  notAvailable: {
    'en-us': 'Not available',
    'de-ch': 'Nicht verfügbar',
    'es-es': 'No disponible',
    'fr-fr': 'Pas disponible',
    'pt-br': 'Não disponível',
    'ru-ru': 'Нет в наличии',
    'uk-ua': 'Не доступно',
  },
} as const);
